import { useState } from 'react';
import { AccountModel, DonViCoSo, KhachHang, KhoHang, Lo, NhanVien, VatTu, ViTriKho } from '@/types/schemas';

export interface SearchFieldState {
  khachHang: KhachHang | null;
  nhanVien: NhanVien | null;
  taiKhoanNo: AccountModel | null;
  vatTu: VatTu | null;
  khoHang: KhoHang | null;
  lo: Lo | null;
  viTri: ViTriKho | null;
  donVi: DonViCoSo | null;
}

export interface SearchFieldActions {
  setKhachHang: (khachHang: KhachHang | null) => void;
  setNhanVien: (nhanVien: NhanVien | null) => void;
  setTaiKhoanNo: (taiKhoanNo: AccountModel | null) => void;
  setVatTu: (vatTu: VatTu | null) => void;
  setKhoHang: (khoHang: KhoHang | null) => void;
  setLo: (lo: Lo | null) => void;
  setViTri: (viTri: ViTriKho | null) => void;
  setDonVi: (donVi: DonViCoSo | null) => void;
  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<SearchFieldState>) => void;
}

export interface UseSearchFieldStatesReturn {
  state: SearchFieldState;
  actions: SearchFieldActions;
}

const initialState: SearchFieldState = {
  khachHang: null,
  nhanVien: null,
  taiKhoanNo: null,
  vatTu: null,
  khoHang: null,
  lo: null,
  viTri: null,
  donVi: null
};

function transformInitialData(initialData?: BanHangRecord): Partial<SearchFieldState> {
  if (!initialData) return {};

  return {
    khachHang: initialData.ma_kh_data || null,
    nhanVien: initialData.ma_nvbh_data || null,
    taiKhoanNo: initialData.tk_data || null,
    vatTu: initialData.ma_vt_data || null,
    khoHang: initialData.ma_kho_data || null,
    lo: initialData.ma_lo_data || null,
    viTri: initialData.ma_vi_tri_data || null,
    donVi: initialData.ma_unit_data || null
  };
}

/**
 * Hook for managing search field states in the hoa-don-ban-hang search dialog
 * Following the pattern from hoa-don-mua-hang-trong-nuoc
 *
 * @param initialData - Optional initial data to populate the state
 * @returns Object with state and actions for search fields
 */
export function useSearchFieldStates(initialData?: BanHangRecord): UseSearchFieldStatesReturn {
  const [state, setState] = useState<SearchFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: SearchFieldActions = {
    setKhachHang: (khachHang: KhachHang | null) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhanVien: (nhanVien: NhanVien | null) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setTaiKhoanNo: (taiKhoanNo: AccountModel | null) => {
      setState(prev => ({
        ...prev,
        taiKhoanNo
      }));
    },

    setVatTu: (vatTu: VatTu | null) => {
      setState(prev => ({
        ...prev,
        vatTu
      }));
    },

    setKhoHang: (khoHang: KhoHang | null) => {
      setState(prev => ({
        ...prev,
        khoHang
      }));
    },

    setLo: (lo: Lo | null) => {
      setState(prev => ({
        ...prev,
        lo
      }));
    },

    setViTri: (viTri: ViTriKho | null) => {
      setState(prev => ({
        ...prev,
        viTri
      }));
    },

    setDonVi: (donVi: DonViCoSo | null) => {
      setState(prev => ({
        ...prev,
        donVi
      }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<SearchFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
